class ProdaPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.purchaseInputs = [...this.querySelectorAll('[data-purchase-type]')];
    this.planSelect = this.querySelector('[data-selling-plan-select]');
    this.planInput = this.querySelector('[data-selling-plan-input]');
    this.details = this.querySelector('[data-subscription-details]');
    this.oneTimePrice = this.querySelector('[data-one-time-price]');
    this.oneTimeComparePrice = this.querySelector('[data-one-time-compare-price]');
    this.oneTimeSavingsValue = this.querySelector('[data-one-time-savings-value]');
    this.oneTimeOfferLabel = this.querySelector('[data-one-time-offer-label]');
    this.subscriptionPrice = this.querySelector('[data-subscription-price]');
    this.subscriptionComparePrice = this.querySelector('[data-subscription-compare-price]');
    this.subscriptionSavingsChip = this.querySelector('[data-subscription-savings-chip]');
    this.subscriptionSavingsValue = this.querySelector('[data-subscription-savings-value]');
    this.packInputs = [...this.querySelectorAll('[data-pack-quantity]')];
    this.packQuantityInput = this.querySelector('[data-pack-quantity-input]');
    this.checkoutPackSizeInput = this.querySelector('[data-checkout-pack-size]');
    this.checkoutPurchaseTypeInput = this.querySelector('[data-checkout-purchase-type]');
    this.packSelection = this.querySelector('[data-pack-selection]');
    this.packAnnouncement = this.querySelector('[data-pack-announcement]');
    this.formComponent = this.closest('product-form-component');
    this.sectionElement = this.closest('.shopify-section');
    this.purchaseTotal = this.sectionElement?.querySelector('[data-proda-purchase-total]');
    this.purchasePack = this.sectionElement?.querySelector('[data-proda-purchase-pack]');
    this.perCanPrice = this.sectionElement?.querySelector('[data-proda-per-can]');
    this.priceContext = this.sectionElement?.querySelector('[data-proda-price-context]');
    this.addToCartButton = this.formComponent?.querySelector('[ref="addToCartButton"]');
    this.addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';
    this.cartQuantity = 0;
    this.quantityMin = 1;
    this.quantityMax = null;
    this.quantityStep = 1;

    this.purchaseInputs.forEach((input) => input.addEventListener('change', this.handlePurchaseChange));
    this.planSelect?.addEventListener('change', this.handlePlanChange);
    this.packInputs.forEach((input) => input.addEventListener('change', this.handlePackChange));
    document.addEventListener('proda:offer-prices-change', this.handleOfferPricesChange);

    this.updateConstraints(
      this.dataset.quantityMin || '1',
      this.dataset.quantityMax || null,
      this.dataset.quantityStep || '1'
    );
  }

  disconnectedCallback() {
    this.purchaseInputs?.forEach((input) => input.removeEventListener('change', this.handlePurchaseChange));
    this.planSelect?.removeEventListener('change', this.handlePlanChange);
    this.packInputs?.forEach((input) => input.removeEventListener('change', this.handlePackChange));
    document.removeEventListener('proda:offer-prices-change', this.handleOfferPricesChange);
  }

  handlePurchaseChange = () => this.sync(true);

  handlePlanChange = () => this.sync(true);

  handleOfferPricesChange = () => this.sync(false);

  handlePackChange = () => {
    this.sync(false);

    const summary = this.purchaseSummary();
    if (this.packAnnouncement) {
      this.announceSelection(summary);
    }

    this.dispatchEvent(
      new CustomEvent('proda:pack-size-change', {
        bubbles: true,
        detail: {
          productId: this.dataset.productId,
          productHandle: this.dataset.productHandle,
          variantId: this.dataset.variantId,
          purchaseType: this.dataset.purchaseType,
          sellingPlanId: this.dataset.sellingPlanId,
          caseQuantity: summary.caseQuantity,
          canCount: summary.canCount,
          unitPriceCents: summary.unitPriceCents,
          valueCents: summary.valueCents,
          savingsPercent: summary.savingsPercent,
        },
      })
    );
  };

  formatMoney(valueCents) {
    const locale = document.documentElement.lang || 'en-US';
    const currency = this.dataset.currencyCode || window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(Number(valueCents || 0) / 100);
    } catch (error) {
      return `$${(Number(valueCents || 0) / 100).toFixed(2)}`;
    }
  }

  announceSelection(summary) {
    if (!this.packAnnouncement) return;
    const purchaseType = summary.isSubscription ? 'Subscription' : 'One-time purchase';
    const delivery = summary.isSubscription && summary.selectedPlan?.dataset.planName
      ? ` ${summary.selectedPlan.dataset.planName}.`
      : '';
    const quote = summary.isSubscription ? null : this.getOneTimeOfferQuote(summary);
    this.packAnnouncement.textContent = `${summary.canCount} cans. ${purchaseType}. Total ${this.formatMoney(quote?.valueCents ?? summary.valueCents)}.${quote?.label ? ` ${quote.label}.` : ''}${delivery}`;
  }

  getOneTimeOfferQuote(summary) {
    if (typeof window.PRODA_OFFER_PRICES?.quote !== 'function') return null;
    return window.PRODA_OFFER_PRICES.quote({
      handle: this.dataset.productHandle,
      variantId: this.dataset.variantId,
      baseCents: Number(this.dataset.oneTimePriceCents || 0),
      quantity: summary.caseQuantity,
      purchaseType: 'one_time',
    });
  }

  purchaseSummary() {
    const selectedPurchase = this.purchaseInputs.find((input) => input.checked);
    const isSubscription = selectedPurchase?.dataset.purchaseType === 'subscription';
    const selectedPlan = this.planSelect?.selectedOptions?.[0];
    const selectedPack = this.packInputs.find((input) => input.checked);
    const caseQuantity = Math.max(1, Number(selectedPack?.value || this.dataset.caseQuantity || 1));
    const cansPerUnit = Math.max(1, Number(this.dataset.cansPerUnit || 12));
    const canCount = Number(selectedPack?.dataset.canCount || caseQuantity * cansPerUnit);
    const subscriptionPriceCents = Number(
      selectedPlan?.dataset.planPriceCents || this.dataset.subscriptionPriceCents || 0
    );
    const oneTimePriceCents = Number(this.dataset.oneTimePriceCents || 0);
    const subscriptionComparePriceCents = Number(selectedPlan?.dataset.planComparePriceCents || oneTimePriceCents);
    const subscriptionSavingsCents = Math.max(0, subscriptionComparePriceCents - subscriptionPriceCents);
    const subscriptionSavingsPercent = subscriptionComparePriceCents > 0
      ? Math.round(subscriptionSavingsCents * 100 / subscriptionComparePriceCents)
      : 0;
    const unitPriceCents = isSubscription ? subscriptionPriceCents : oneTimePriceCents;

    return {
      isSubscription,
      selectedPlan,
      caseQuantity,
      canCount,
      unitPriceCents,
      valueCents: unitPriceCents * caseQuantity,
      subscriptionComparePriceCents,
      subscriptionSavingsCents,
      subscriptionSavingsPercent,
      savingsPercent: isSubscription ? subscriptionSavingsPercent : 0,
    };
  }

  getValue() {
    return String(this.purchaseSummary().caseQuantity);
  }

  setValue(value) {
    const input = this.packInputs.find((packInput) => packInput.value === String(value));
    if (!input || input.disabled) return;
    input.checked = true;
    this.sync(false);
  }

  setCartQuantity(cartQuantity) {
    this.cartQuantity = Math.max(0, Number(cartQuantity || 0));
  }

  canAddToCart() {
    const quantityToAdd = this.purchaseSummary().caseQuantity;
    const wouldExceedMax = this.quantityMax !== null && this.cartQuantity + quantityToAdd > this.quantityMax;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: this.quantityMax,
      cartQuantity: this.cartQuantity,
      quantityToAdd,
    };
  }

  updateConstraints(min, max, step) {
    this.quantityMin = Math.max(1, Number(min || 1));
    this.quantityMax = max ? Number(max) : null;
    this.quantityStep = Math.max(1, Number(step || 1));

    this.packInputs.forEach((input) => {
      const quantity = Number(input.value);
      const belowMin = quantity < this.quantityMin;
      const aboveMax = this.quantityMax !== null && quantity > this.quantityMax;
      const offStep = (quantity - this.quantityMin) % this.quantityStep !== 0;
      input.disabled = belowMin || aboveMax || offStep;
      input.closest('.proda-pack-size__option')?.classList.toggle('is-disabled', input.disabled);
    });

    const selected = this.packInputs.find((input) => input.checked && !input.disabled);
    if (!selected) {
      const firstAvailable = this.packInputs.find((input) => !input.disabled);
      if (firstAvailable) firstAvailable.checked = true;
    }
    this.sync(false);
  }

  sync(shouldTrack) {
    const summary = this.purchaseSummary();
    const { isSubscription, selectedPlan, caseQuantity, canCount } = summary;
    const oneTimeQuote = this.getOneTimeOfferQuote(summary);
    const displayedValueCents = !isSubscription && oneTimeQuote ? oneTimeQuote.valueCents : summary.valueCents;

    this.querySelectorAll('.proda-purchase-option').forEach((option) => {
      option.classList.toggle('is-selected', Boolean(option.querySelector('input:checked')));
    });
    this.querySelectorAll('.proda-pack-size__option').forEach((option) => {
      option.classList.toggle('is-selected', Boolean(option.querySelector('input:checked')));
    });

    if (this.details) this.details.hidden = !isSubscription;
    if (this.planInput) this.planInput.value = isSubscription ? this.planSelect?.value || '' : '';
    if (this.oneTimePrice) {
      this.oneTimePrice.textContent = this.formatMoney(oneTimeQuote?.valueCents ?? Number(this.dataset.oneTimePriceCents || 0) * caseQuantity);
    }
    if (this.oneTimeComparePrice) {
      this.oneTimeComparePrice.hidden = !oneTimeQuote || oneTimeQuote.savingsCents <= 0;
      this.oneTimeComparePrice.textContent = oneTimeQuote ? this.formatMoney(oneTimeQuote.originalCents) : '';
      this.oneTimeComparePrice.setAttribute('aria-label', `Original price: ${this.oneTimeComparePrice.textContent}`);
    }
    if (this.oneTimeSavingsValue) {
      this.oneTimeSavingsValue.hidden = !oneTimeQuote || oneTimeQuote.savingsCents <= 0;
      this.oneTimeSavingsValue.textContent = oneTimeQuote ? `Save ${this.formatMoney(oneTimeQuote.savingsCents)}` : '';
    }
    if (this.oneTimeOfferLabel) {
      this.oneTimeOfferLabel.hidden = !oneTimeQuote?.label;
      this.oneTimeOfferLabel.textContent = oneTimeQuote?.label || '';
    }
    if (this.subscriptionPrice) {
      const subscriptionUnitPrice = Number(
        selectedPlan?.dataset.planPriceCents || this.dataset.subscriptionPriceCents || 0
      );
      this.subscriptionPrice.textContent = this.formatMoney(subscriptionUnitPrice * caseQuantity);
    }
    if (this.subscriptionComparePrice) {
      this.subscriptionComparePrice.hidden = summary.subscriptionSavingsCents <= 0;
      this.subscriptionComparePrice.textContent = this.formatMoney(summary.subscriptionComparePriceCents * caseQuantity);
      this.subscriptionComparePrice.setAttribute('aria-label', `One-time price: ${this.subscriptionComparePrice.textContent}`);
    }
    if (this.subscriptionSavingsChip) {
      this.subscriptionSavingsChip.hidden = summary.subscriptionSavingsPercent <= 0;
      this.subscriptionSavingsChip.textContent = `Save ${summary.subscriptionSavingsPercent}%`;
    }
    if (this.subscriptionSavingsValue) {
      this.subscriptionSavingsValue.hidden = summary.subscriptionSavingsCents <= 0;
      this.subscriptionSavingsValue.textContent = `Save ${this.formatMoney(summary.subscriptionSavingsCents * caseQuantity)} per delivery`;
    }
    for (const planOption of this.planSelect?.options || []) {
      const planName = (planOption.dataset.planDisplayName || planOption.dataset.planName || '').replace(/^deliver\s+every\s+/i, 'Every ');
      const comparePrice = Number(planOption.dataset.planComparePriceCents || this.dataset.oneTimePriceCents || 0);
      const savingsPercent = comparePrice > 0
        ? Math.max(0, Math.round((comparePrice - Number(planOption.dataset.planPriceCents || 0)) * 100 / comparePrice))
        : 0;
      planOption.textContent = `${planName}${savingsPercent > 0 ? ` · Save ${savingsPercent}%` : ''}`;
    }
    if (this.packQuantityInput) this.packQuantityInput.value = String(caseQuantity);
    if (this.checkoutPackSizeInput) this.checkoutPackSizeInput.value = `${canCount} cans`;
    if (this.checkoutPurchaseTypeInput) {
      this.checkoutPurchaseTypeInput.value = isSubscription ? 'Subscription' : 'One-time';
    }
    if (this.packSelection) this.packSelection.textContent = `${canCount} cans`;
    if (this.purchaseTotal) this.purchaseTotal.textContent = this.formatMoney(displayedValueCents);
    if (this.purchasePack) this.purchasePack.textContent = `${canCount} cans`;
    if (this.perCanPrice) this.perCanPrice.textContent = this.formatMoney(Math.round(displayedValueCents / canCount));
    if (this.priceContext) this.priceContext.textContent = isSubscription ? 'subscription' : 'one-time';
    if (this.formComponent) this.formComponent.dataset.quantityDefault = String(caseQuantity);
    if (this.addToCartButton && !this.addToCartButton.disabled) {
      this.addToCartButton.setAttribute(
        'aria-label',
        `${this.addToCartLabel}, ${canCount} cans for ${this.formatMoney(displayedValueCents)}, ${isSubscription ? 'subscription' : 'one-time purchase'}${!isSubscription && oneTimeQuote?.label ? `, ${oneTimeQuote.label}` : ''}`
      );
    }

    this.dataset.purchaseType = isSubscription ? 'subscription' : 'one_time';
    this.dataset.sellingPlanId = isSubscription ? this.planSelect?.value || '' : '';
    this.dataset.sellingPlanName = isSubscription ? selectedPlan?.dataset.planName || '' : '';
    this.dataset.subscriptionPriceCents =
      selectedPlan?.dataset.planPriceCents || this.dataset.subscriptionPriceCents || '';
    this.dataset.savingsPercent = isSubscription ? String(summary.savingsPercent) : '0';
    this.dataset.caseQuantity = String(caseQuantity);
    this.dataset.canCount = String(canCount);

    this.dispatchEvent(
      new CustomEvent('quantity-selector:update', {
        bubbles: true,
        detail: { quantity: caseQuantity },
      })
    );

    if (!shouldTrack) return;

    this.announceSelection(summary);

    document.dispatchEvent(
      new CustomEvent('proda:purchase-option-change', {
        detail: {
          productId: this.dataset.productId,
          productHandle: this.dataset.productHandle,
          variantId: this.dataset.variantId,
          purchaseType: this.dataset.purchaseType,
          sellingPlanId: this.dataset.sellingPlanId,
          sellingPlanName: this.dataset.sellingPlanName,
          caseQuantity,
          canCount,
          unitPriceCents: summary.unitPriceCents,
          valueCents: summary.valueCents,
          savingsPercent: summary.savingsPercent,
        },
      })
    );
  }
}

if (!customElements.get('proda-purchase-options')) {
  customElements.define('proda-purchase-options', ProdaPurchaseOptions);
}
